import { useRef, useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, FileText, Loader2, Pencil, Sparkles, Trophy } from 'lucide-react';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { InsightCard } from '@/features/campaign-analysis/components/insight-card';
import { Button } from '@/shared/components/shadcn/button';
import { Badge } from '@/shared/components/shadcn/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/shared/components/shadcn/sheet';
import { cn } from '@/shared/utils';
import type { InsightCard as InsightCardType, PlanningReport, PostReference, VideoPost } from '@/features/campaign-analysis/types';
import { PlanningBriefSheet } from './planning-brief-sheet';

function localizeInsight(insight: InsightCardType, isKo: boolean): InsightCardType {
  if (isKo) return insight;
  return {
    ...insight,
    title: insight.titleEn ?? insight.title,
    detail: insight.detailEn ?? insight.detail,
    uplift: insight.upliftEn ?? insight.uplift,
    tags: insight.tagsEn ?? insight.tags,
  };
}

function localizePost(post: VideoPost, isKo: boolean): VideoPost {
  if (isKo) return post;
  return {
    ...post,
    title: post.titleEn ?? post.title,
    description: post.descriptionEn ?? post.description,
    reasons: post.reasonsEn ?? post.reasons,
  };
}

interface BrandInfo {
  brandName: string;
  productName: string;
  description: string;
}

interface InputSummary {
  country: string;
  platform: string;
  brandProduct: string;
  metric: string;
}

interface Props {
  report: PlanningReport;
  inputSummary: InputSummary;
  benchmarkProductCount: number;
}

function PostVideo({
  post,
  className,
}: {
  post: PostReference;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleMouseEnter = () => videoRef.current?.play();
  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className={cn('group relative overflow-hidden rounded-lg', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        className="w-full object-cover"
        poster={post.posterUrl}
        muted
        playsInline
        loop
        controls
        style={{ aspectRatio: '9/16' }}
      >
        <source src={post.videoUrl} type="video/mp4" />
      </video>
      <a
        href={post.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute right-2 top-2 flex size-6 items-center justify-center rounded-md bg-background/80 text-muted-foreground hover:text-foreground transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className="size-3.5" />
      </a>
    </div>
  );
}

function VideoPostCard({ post, isKo }: { post: VideoPost; isKo: boolean }) {
  return (
    <div className="flex gap-4 rounded-xl border bg-card p-4">
      <div className="w-[160px] shrink-0">
        <PostVideo
          post={{ videoUrl: post.videoUrl, posterUrl: post.posterUrl, sourceUrl: post.sourceUrl }}
        />
      </div>

      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex items-start gap-2">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
            {post.rank}
          </span>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold leading-snug text-foreground">{post.title}</p>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="secondary" className="text-xs font-normal">
                {post.gmv}
              </Badge>
              <Badge variant="secondary" className="text-xs font-normal">
                {post.creatorProfile}
              </Badge>
            </div>
          </div>
        </div>

        <p className="text-sm leading-relaxed text-muted-foreground">{post.description}</p>

        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-foreground">Why it performed well</p>
          <ul className="flex flex-col gap-1.5">
            {post.reasons.map((reason, i) => (
              <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                <span className="mt-1 shrink-0 text-primary">·</span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function getRecommendReason(globalIdx: number, brandName: string, productName: string, isKo: boolean): string {
  const ko: Record<number, string> = {
    2: `${productName}의 실제 효능을 증명하는 가장 강력한 조합 전략입니다. 전환율 극대화에 핵심입니다.`,
    3: `${productName}의 차별점을 갈등 훅으로 전달해 첫 3초 이탈을 방지합니다.`,
    4: `${brandName} 타겟 고객(뷰티·웰니스 관심 MZ세대)에게 가장 공감받는 크리에이터 프로파일입니다.`,
    5: `${productName}의 흔한 실수를 피하고 신뢰도를 높이는 데 직접적으로 적용 가능한 인사이트입니다.`,
  };
  const en: Record<number, string> = {
    2: `The most powerful combination strategy to prove ${productName}'s efficacy. Critical for maximizing conversions.`,
    3: `Delivers ${productName}'s differentiators via a conflict hook to prevent first-3-second drop-offs.`,
    4: `The creator profile that resonates most with ${brandName}'s target audience (beauty & wellness Gen Z and Millennials).`,
    5: `Directly applicable to avoid common mistakes and build trust when promoting ${productName}.`,
  };
  return isKo ? (ko[globalIdx] ?? '') : (en[globalIdx] ?? '');
}

export function PlanningResults({ report, inputSummary, benchmarkProductCount }: Props) {
  const { workspace } = useWorkspace();
  const isKo = workspace.language === 'ko';

  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [videoOpenIndices, setVideoOpenIndices] = useState<Set<number>>(new Set());
  const [briefOpen, setBriefOpen] = useState(false);

  // Brand info + recommendation state
  const [brandInfoOpen, setBrandInfoOpen] = useState(false);
  const [brandInfo, setBrandInfo] = useState<BrandInfo | null>(null);
  const [brandForm, setBrandForm] = useState<BrandInfo>({
    brandName: 'Syncly',
    productName: 'Collagen Apple Gummy',
    description:
      '- Hydration and elasticity in one\n- Fast-absorbing formula\n- Refreshing taste, low-calorie and guilt-free\n- Collagen extracted from pure natural ingredients\n- No sweeteners / No artificial flavors',
  });
  const [recommendedIndices, setRecommendedIndices] = useState<Set<number>>(new Set());
  const [recommendedReasons, setRecommendedReasons] = useState<Map<number, string>>(new Map());
  const [recommendLoading, setRecommendLoading] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);

  const toggleInsight = (idx: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleVideo = (idx: number) => {
    setVideoOpenIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleRecommend = async () => {
    setBrandInfoOpen(false);
    setRecommendLoading(true);

    await new Promise((resolve) => setTimeout(resolve, 500));

    const info = brandForm;
    setBrandInfo(info);

    const newRecommended = new Set<number>();
    const newReasons = new Map<number, string>();

    const RECOMMENDED_INDICES = [2, 3, 4, 5];
    RECOMMENDED_INDICES.forEach((idx) => {
      if (idx < allInsights.length) {
        newRecommended.add(idx);
        newReasons.set(idx, getRecommendReason(idx, info.brandName || info.productName, info.productName || info.brandName, isKo));
      }
    });

    setRecommendedIndices(newRecommended);
    setRecommendedReasons(newReasons);
    setSelectedIndices(new Set(newRecommended));
    setRecommendLoading(false);
  };

  const handleOpenBrief = async () => {
    setBriefLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setBriefLoading(false);
    setBriefOpen(true);
  };

  const allInsights = report.insights.map((i) => localizeInsight(i, isKo));
  const doInsights = allInsights.filter((i) => i.type === 'do');
  const dontInsights = allInsights.filter((i) => i.type === 'dont');
  const selectedInsights = [...selectedIndices].sort().map((i) => allInsights[i]);
  const localizedPosts = report.topVideoPosts.map((p) => localizePost(p, isKo));

  const renderInsightList = (type: 'do' | 'dont') =>
    allInsights
      .map((insight, globalIdx) => ({ insight, globalIdx }))
      .filter(({ insight }) => insight.type === type)
      .map(({ insight, globalIdx }) => {
        const hasPosts = !!insight.referencePosts?.length;
        const videosOpen = videoOpenIndices.has(globalIdx);
        const isRecommended = recommendedIndices.has(globalIdx);
        const recommendedReason = recommendedReasons.get(globalIdx);
        return (
          <InsightCard
            key={globalIdx}
            insight={insight}
            isSelectable
            isSelected={selectedIndices.has(globalIdx)}
            onToggle={() => toggleInsight(globalIdx)}
            isRecommended={isRecommended}
            recommendedReason={recommendedReason}
            action={
              hasPosts ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleVideo(globalIdx);
                  }}
                  className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  {videosOpen ? (
                    <>
                      <ChevronUp className="size-3" />
                      {isKo ? '영상 닫기' : 'Close videos'}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="size-3" />
                      {isKo ? `영상 보기 (${insight.referencePosts!.length})` : `View videos (${insight.referencePosts!.length})`}
                    </>
                  )}
                </button>
              ) : undefined
            }
            expandedContent={
              hasPosts && videosOpen ? (
                <div className="flex gap-3 overflow-x-auto pb-1 pt-1">
                  {insight.referencePosts!.map((ref) => (
                    <PostVideo
                      key={ref.videoUrl}
                      post={ref}
                      className="h-[350px] w-[197px] shrink-0"
                    />
                  ))}
                </div>
              ) : undefined
            }
          />
        );
      });

  return (
    <div className="relative flex gap-6 pb-24">
      {/* Left: insights + top video posts */}
      <div className="flex min-w-0 flex-1 flex-col gap-8">
        {/* Total insights */}
        <section className="flex flex-col gap-4">
          <div className="flex items-end justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-semibold text-foreground">Total insights</h2>
              <p className="text-sm text-muted-foreground">
                {isKo
                  ? '카드를 선택해 Content Brief를 생성하세요'
                  : 'Select cards to generate a Content Brief'}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {selectedIndices.size > 0 && (
                <span className="text-xs text-muted-foreground">
                  {isKo ? `${selectedIndices.size}개 선택됨` : `${selectedIndices.size} selected`}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-xs h-8"
                onClick={() => setBrandInfoOpen(true)}
              >
                <Sparkles className="size-3.5" />
                {isKo ? '내 브랜드 맞춤 추천' : 'Get recommendations'}
              </Button>
            </div>
          </div>

          {doInsights.length > 0 && (
            <div className="flex flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600">
                What Works ({doInsights.length})
              </p>
              <div className="flex flex-col gap-3">{renderInsightList('do')}</div>
            </div>
          )}

          {dontInsights.length > 0 && (
            <div className="flex flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-rose-600">
                What Doesn't Work ({dontInsights.length})
              </p>
              <div className="flex flex-col gap-3">{renderInsightList('dont')}</div>
            </div>
          )}
        </section>

        {/* Top 5 video insights */}
        <section className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Trophy className="size-4 text-amber-500" />
            <h2 className="text-base font-semibold text-foreground">Top 5 video insights</h2>
          </div>
          <p className="text-sm text-muted-foreground -mt-2">
            {isKo
              ? 'Revenue score 기준 최고 성과 포스트 5건. 각 영상에서 공통적으로 관찰된 성공 패턴을 확인하세요.'
              : 'Top 5 posts by Revenue score. Review the common success patterns observed across each video.'}
          </p>
          <div className="flex flex-col gap-4">
            {localizedPosts.map((post) => (
              <VideoPostCard key={post.rank} post={post} isKo={isKo} />
            ))}
          </div>
        </section>
      </div>

      {/* Right: sticky sidebar */}
      <div className="w-64 shrink-0">
        <div className="sticky top-4 flex flex-col gap-4 rounded-xl border bg-card p-5">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{isKo ? '벤치마크 상품' : 'Benchmarks'}</span>
              <span className="font-medium">{benchmarkProductCount}{isKo ? '개' : ''}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{isKo ? '분석 포스트' : 'Posts analyzed'}</span>
              <span className="font-medium">{report.postsAnalyzed.toLocaleString()}{isKo ? '개' : ''}</span>
            </div>
          </div>
          <div className="border-t pt-3 flex flex-col gap-1.5">
            <p className="text-xs text-muted-foreground">{isKo ? '입력 조건' : 'Input conditions'}</p>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="secondary" className="text-xs font-normal">
                {inputSummary.country}
              </Badge>
              <Badge variant="secondary" className="text-xs font-normal">
                {inputSummary.platform}
              </Badge>
              <Badge variant="secondary" className="text-xs font-normal">
                {inputSummary.brandProduct}
              </Badge>
              <Badge variant="secondary" className="text-xs font-normal">
                {inputSummary.metric}
              </Badge>
            </div>
          </div>
          {brandInfo && (
            <div className="border-t pt-3 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{isKo ? '브랜드 정보' : 'Brand info'}</p>
                <button
                  type="button"
                  onClick={() => setBrandInfoOpen(true)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Pencil className="size-3" />
                  {isKo ? '수정' : 'Edit'}
                </button>
              </div>
              <div className="flex flex-col gap-0.5">
                <p className="text-xs font-medium text-foreground">{brandInfo.brandName}</p>
                <p className="text-xs text-muted-foreground">{brandInfo.productName}</p>
              </div>
              {brandInfo.description && (
                <p className="text-xs leading-relaxed text-muted-foreground line-clamp-3">
                  {brandInfo.description}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Floating Content Brief button */}
      {selectedIndices.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <Button
            onClick={handleOpenBrief}
            disabled={briefLoading}
            className="gap-2 shadow-lg shadow-primary/20 px-5"
            size="lg"
          >
            {briefLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <FileText className="size-4" />
            )}
            {briefLoading
              ? (isKo ? '생성 중...' : 'Generating...')
              : isKo
                ? `Content Brief 생성 (${selectedIndices.size}개)`
                : `Generate Content Brief (${selectedIndices.size})`}
          </Button>
        </div>
      )}

      {/* Brand info sheet */}
      <Sheet open={brandInfoOpen} onOpenChange={setBrandInfoOpen}>
        <SheetContent className="flex w-full flex-col gap-0 overflow-hidden sm:max-w-md">
          <SheetHeader className="shrink-0 border-b pb-4">
            <SheetTitle className="text-base">
              {isKo ? '내 브랜드 맞춤 인사이트 추천' : 'Get recommendations for your brand'}
            </SheetTitle>
            <p className="text-xs text-muted-foreground">
              {isKo
                ? '브랜드·상품 정보를 입력하면 가장 적합한 인사이트를 추천해드립니다.'
                : 'Enter your brand and product info to receive personalized insight recommendations.'}
            </p>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-5">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-foreground">
                {isKo ? '브랜드명' : 'Brand name'}
                <span className="ml-1 text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={brandForm.brandName}
                onChange={(e) => setBrandForm((f) => ({ ...f, brandName: e.target.value }))}
                placeholder={isKo ? '예: 오니스트' : 'e.g. Ownist'}
                className={cn(
                  'w-full rounded-lg border bg-background px-3 py-2 text-sm',
                  'text-foreground placeholder:text-muted-foreground/60',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0',
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-foreground">
                {isKo ? '프로덕트명' : 'Product name'}
                <span className="ml-1 text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={brandForm.productName}
                onChange={(e) => setBrandForm((f) => ({ ...f, productName: e.target.value }))}
                placeholder={isKo ? '예: 콜라겐 드링크 믹스' : 'e.g. Collagen Drink Mix'}
                className={cn(
                  'w-full rounded-lg border bg-background px-3 py-2 text-sm',
                  'text-foreground placeholder:text-muted-foreground/60',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0',
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-foreground">
                {isKo ? '프로덕트 설명' : 'Product description'}
                <span className="ml-1 text-xs text-muted-foreground">{isKo ? '(선택)' : '(optional)'}</span>
              </label>
              <textarea
                value={brandForm.description}
                onChange={(e) => setBrandForm((f) => ({ ...f, description: e.target.value }))}
                placeholder={isKo
                  ? '주요 성분, 효능, 타겟 고객 등을 간략히 입력하세요'
                  : 'Briefly describe key ingredients, benefits, and target audience'}
                rows={8}
                className={cn(
                  'w-full resize-none rounded-lg border bg-background px-3 py-2 text-sm',
                  'text-foreground placeholder:text-muted-foreground/60',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0',
                )}
              />
            </div>
          </div>

          <div className="shrink-0 border-t p-5">
            <Button
              className="w-full gap-2"
              disabled={!brandForm.brandName.trim() || !brandForm.productName.trim() || recommendLoading}
              onClick={handleRecommend}
            >
              {recommendLoading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Sparkles className="size-4" />
              )}
              {recommendLoading
                ? (isKo ? '분석 중...' : 'Analyzing...')
                : (isKo ? '추천 인사이트 보기' : 'Show recommended insights')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <PlanningBriefSheet
        open={briefOpen}
        onOpenChange={setBriefOpen}
        insights={selectedInsights}
        brandInfo={brandInfo}
        creatorGuidePrompts={report.creatorGuidePrompts ?? []}
      />
    </div>
  );
}
